// ===========================================
// READY FOR SERVICE
// ===========================================
// One calm setup checklist on the dashboard: what's left before the first
// real shift. Collapsible, and disappears entirely once everything is done —
// never a wall of onboarding.

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { CheckCircle2, Circle, ChevronDown, ChevronRight, Sparkles } from 'lucide-react';
import { onboardingApi, restaurantApi } from '../services/api';

const COLLAPSED_KEY = 'mise-ready-for-service-collapsed';

interface StepDef {
  key: string;
  label: string;
  hint: string;
  to?: string; // no link = do it on another device (POS)
}

const STEPS: StepDef[] = [
  { key: 'menu', label: 'Add your menu', hint: 'Categories, items, prices', to: '/menu' },
  { key: 'tables', label: 'Set up your floor plan', hint: 'Sections and tables', to: '/floor-plan' },
  { key: 'staff', label: 'Add your team', hint: 'Everyone who works a shift', to: '/staff' },
  { key: 'payouts', label: 'Connect your bank', hint: 'Card payments land in your account', to: '/settings?tab=payments' },
  { key: 'printers', label: 'Point at your printers', hint: 'Receipts and kitchen tickets', to: '/settings?tab=devices' },
  { key: 'firstOrder', label: 'Ring in a test order', hint: 'Open Mise POS and try a table end to end' },
];

export default function ReadyForServiceCard() {
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(COLLAPSED_KEY) === 'true'
  );

  // Makers (bakeries, CPG) have no floor plan / menu / test order — the
  // restaurant "ready for service" checklist doesn't apply to them. Keyed per
  // account so one account's modules never bleed into another's cache.
  const currentRestaurantId = (() => {
    try { return JSON.parse(localStorage.getItem('manager_current_restaurant') || 'null')?.id || null; } catch { return null; }
  })();
  const { data: modulesData } = useQuery({
    queryKey: ['restaurant-modules', currentRestaurantId],
    queryFn: async () => ((await restaurantApi.getCurrent()).data?.data?.settings?.modules || {}) as Record<string, boolean>,
    staleTime: 30 * 1000, refetchOnWindowFocus: true,
  });
  // Only a production-enabled account is a maker. Never infer "maker" from a
  // restaurant module merely being off — that wrongly hid this card for others.
  const isMaker = modulesData?.production === true;

  const { data } = useQuery({
    queryKey: ['onboarding-status'],
    queryFn: async () => (await onboardingApi.getStatus()).data,
    staleTime: 60_000,
    enabled: !isMaker,
  });

  if (isMaker) return null;

  const ready = data?.data?.readyForService as Record<string, boolean> | undefined;
  if (!ready) return null;

  const doneCount = STEPS.filter((s) => ready[s.key]).length;
  if (doneCount === STEPS.length) return null; // all set — get out of the way

  const toggle = () => {
    const next = !collapsed;
    setCollapsed(next);
    localStorage.setItem(COLLAPSED_KEY, String(next));
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <button
        onClick={toggle}
        className="w-full px-4 py-3 flex items-center justify-between gap-3 text-left"
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-lg bg-primary-50 flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5 text-primary-600" />
          </div>
          <div className="min-w-0">
            <p className="font-semibold text-slate-900">Ready for service</p>
            <p className="text-xs text-slate-500">
              {doneCount} of {STEPS.length} done — this card disappears when you're set
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <div className="hidden sm:block w-24 h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full bg-primary-500 rounded-full transition-all"
              style={{ width: `${(doneCount / STEPS.length) * 100}%` }}
            />
          </div>
          {collapsed ? (
            <ChevronRight className="w-4 h-4 text-slate-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-slate-400" />
          )}
        </div>
      </button>

      {!collapsed && (
        <div className="border-t border-slate-100 divide-y divide-slate-50">
          {STEPS.map((step) => {
            const done = !!ready[step.key];
            const row = (
              <div className="px-4 py-2.5 flex items-center gap-3">
                {done ? (
                  <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0" />
                ) : (
                  <Circle className="w-5 h-5 text-slate-300 shrink-0" />
                )}
                <div className="min-w-0 flex-1">
                  <p className={done ? 'text-sm text-slate-400 line-through' : 'text-sm font-medium text-slate-800'}>
                    {step.label}
                  </p>
                  {!done && <p className="text-xs text-slate-400">{step.hint}</p>}
                </div>
                {!done && step.to && <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />}
              </div>
            );
            return step.to && !done ? (
              <Link key={step.key} to={step.to} className="block hover:bg-slate-50 transition-colors">
                {row}
              </Link>
            ) : (
              <div key={step.key}>{row}</div>
            );
          })}
        </div>
      )}
    </div>
  );
}
